"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VectorManualFieldView = void 0;
exports.determineSpanLinkType = determineSpanLinkType;
exports.viewVectorManualEdit = viewVectorManualEdit;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const IkeModel = tslib_1.__importStar(require("@ikegps/core-shared"));
const core_shared_1 = require("@ikegps/core-shared");
const field_action_button_1 = require("../../../components/button/field.action.button");
const icons_1 = require("../../../components/icon/icons");
const input_1 = require("../../../components/input/input");
const vector_input_1 = require("../../../components/input/vector.input");
const vector_input_state_1 = require("../../../components/input/vector.input.state");
const notification_service_1 = require("../../../components/notification.toast/notification.service");
const theme_1 = require("../../../components/theme");
const index_1 = require("../../../i18n/index");
const mithril_1 = require("../../../mithril");
const topological_editor_collection_edit_ctrl_1 = require("../../../page/topological.editor.page/topological.editor.collection.edit.ctrl");
const services_1 = require("../../../services");
const page_state_1 = require("../../../state/page.state");
const store_1 = require("../../../store");
const field_info_1 = require("../../field.info/field.info");
const span_link_modal_1 = require("../../span.link.modal/span.link.modal");
const point_collection_cache_1 = require("../../topological.editor.map/point.collection.cache");
const span_link_validator_1 = require("../../../services/subform.link/span.link.validator");
const field_action_button_2 = require("../../../components/button/field.action.button");
const collection_edit_model_1 = require("../../../store/collection.edit.model");
const CSS = {
    container: 'c-Vector',
    isLinked: 'c-Vector--linked',
};
(0, theme_1.addStyles)({
    [CSS.isLinked]: {
        display: 'flex',
        'justify-content': 'flex-end',
    },
    [`${CSS.container} .${field_action_button_2.CSS.container}`]: {
        'margin-right': '4px',
    },
});
/**
 * Hybrid check for span vector field.
 * Uses IPoleForm when available (for richer features),
 * falls back to generic SubFormType.SPAN detection for other forms.
 *
 * Returns true only if:
 * 1. The field is inside a subform with SubFormType.SPAN
 * 2. The field is the designated span vector field for that subform
 * 3. The subform has complete span type state
 */
function isSpanVectorField(field, ctrl) {
    if (ctrl.collection == null) {
        return false;
    }
    // First, verify this field is actually inside a SPAN-typed subform
    if (!field.isPartOfSpanSubForm()) {
        return false;
    }
    // Next, verify the span subform has complete type state. This ensures all
    //   required fields are configured as the two way link adapts in the future
    // We know that this will be the parent form as the span subform type can only be applied to a first level subform
    if (!(0, core_shared_1.hasCompleteSpanTypeState)(field.parent.schema)) {
        return false;
    }
    return true;
}
/**
 * Determines whether to create a two-way or one-way span link.
 * Two-way links require:
 * 1. The field must be in a SPAN subform context
 * 2. Source and target form versions must match
 */
function determineSpanLinkType(field, ctrl, sourceFormRevision, targetCollectionModel) {
    if (!isSpanVectorField(field, ctrl)) {
        return { type: 'one-way', reason: 'not-span-context' };
    }
    if (sourceFormRevision !== targetCollectionModel.schema.revision) {
        return { type: 'one-way', reason: 'version-mismatch' };
    }
    return { type: 'two-way' };
}
function linkCollection(field, ctrl) {
    return tslib_1.__awaiter(this, void 0, void 0, function* () {
        var _a;
        if (!ctrl.collection) {
            return;
        }
        const isSpanContext = isSpanVectorField(field, ctrl) && services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_GROUP_IKE_ANALYZE);
        services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, isSpanContext ? 'span:link:start' : 'vector:link:start', field.schema.displayName, 1);
        notification_service_1.NotificationService.info({ key: index_1.T.COLLECTION_LINK_START });
        const sourceFormRevision = ctrl.collection.schema.revision;
        // Pass context to state BEFORE calling chooseLinkingCollection
        page_state_1.CurrentState.CollectionLink.beginLinking(field.id, {
            formRevision: sourceFormRevision,
            isSpanContext,
        });
        const targetCollection = yield ctrl.chooseLinkingCollection(field);
        if (targetCollection == null) {
            services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, isSpanContext ? 'span:link:cancel' : 'vector:link:cancel', field.schema.displayName, 1);
            return;
        }
        if (targetCollection.uuid === ctrl.collection.uuid) {
            notification_service_1.NotificationService.warn({ key: index_1.T.COLLECTION_LINK_INVALID });
            return;
        }
        // Determine link type using summary's formRevision
        const linkDecision = determineSpanLinkType(field, ctrl, sourceFormRevision, targetCollection);
        if (linkDecision.type === 'one-way' && linkDecision.reason === 'version-mismatch') {
            notification_service_1.NotificationService.warn({ key: index_1.T.SPAN_LINK_VERSION_MISMATCH });
        }
        if (linkDecision.type === 'two-way') {
            const validation = (0, span_link_validator_1.validateSpanLink)(ctrl.collection, targetCollection.id, field);
            if (!validation.valid) {
                notification_service_1.NotificationService.error({
                    key: index_1.T.SPAN_LINK_DUPLICATE_ERROR,
                    attrs: { existingSpan: validation.existingSpan },
                });
                services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'span:link:duplicate-blocked', field.schema.displayName, 1);
                return;
            }
            // Load target collection to display in modal
            const targetCollectionModel = yield store_1.AppStore.Collection.fetch(targetCollection.revision);
            if (!targetCollectionModel) {
                notification_service_1.NotificationService.error({ key: index_1.T.ERROR_GENERAL });
                return;
            }
            // Get the source span instance ID (the parent of the vector field)
            const sourceSpanInstanceId = field.parent.instanceId;
            if (!sourceSpanInstanceId || !ctrl.collection) {
                notification_service_1.NotificationService.error({ key: index_1.T.ERROR_GENERAL });
                return;
            }
            // Open the span selection modal
            const modalResult = yield (0, span_link_modal_1.openSpanLinkModal)(ctrl.collection, targetCollectionModel, sourceSpanInstanceId);
            if (modalResult.cancelled) {
                services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'span:link:modal:cancel', field.schema.displayName, 1);
                return;
            }
            const spanSubForm = ctrl.collection.spanSubForm;
            const spanInstance = spanSubForm === null || spanSubForm === void 0 ? void 0 : spanSubForm.getInstance(sourceSpanInstanceId);
            if (!spanInstance) {
                notification_service_1.NotificationService.error({ key: index_1.T.ERROR_GENERAL });
                return;
            }
            const span = new IkeModel.SpanSubFormAdaptor(spanInstance);
            // Perform two-way span link
            try {
                if (!modalResult.createNew && !modalResult.selectedSpanId) {
                    services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'span:link:two-way:error', 'no-selection', 1);
                    notification_service_1.NotificationService.error({ key: index_1.T.SPAN_LINK_ERROR });
                    return;
                }
                // Put target collection into edit mode so mutations go to a proxy state.
                // This allows us to revert the target if Pole A's changes are discarded.
                if (topological_editor_collection_edit_ctrl_1.TopologicalEditorCollectionEditCtrl.is(ctrl)) {
                    // Topo editor: register as edit controller so sidebar shows "(unsaved)"
                    // and the collection view displays the updated spans
                    ctrl.registerCollectionForEditing(targetCollectionModel);
                }
                else {
                    // Map page: use standalone proxy for rollback
                    targetCollectionModel.beginEdit((state) => new collection_edit_model_1.CollectionModelStateEdit(state));
                }
                const result = services_1.Services.SubFormLink.performTwoWaySpanLink({
                    sourceSpanSubForm: span,
                    sourceCollection: ctrl.collection,
                    targetCollection: targetCollectionModel,
                    targetInstanceId: modalResult.createNew ? undefined : ((_a = modalResult.selectedSpanId) !== null && _a !== void 0 ? _a : undefined),
                });
                // Store pending link info for save ordering (Slice 07)
                page_state_1.CurrentState.CollectionLink.addPendingTwoWayLink({
                    sourceCollection: result.sourceCollection,
                    targetCollection: result.targetCollection,
                    sourceSpanInstance: result.sourceSpanInstance,
                    targetSpanInstance: result.targetSpanInstance,
                });
                // Invalidate topo cache for both collections so vectors are recomputed with updated positions
                if (topological_editor_collection_edit_ctrl_1.TopologicalEditorCollectionEditCtrl.is(ctrl) && ctrl.collectionEditState.collectionRevision) {
                    point_collection_cache_1.TopologicalPointCollectionCache.dropCollectionVectors(ctrl.collectionEditState.collectionRevision);
                    point_collection_cache_1.TopologicalPointCollectionCache.dropCollectionVectors(targetCollectionModel.revision);
                    ctrl.redrawMap();
                }
                services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'span:link:two-way', field.schema.displayName, 1);
                notification_service_1.NotificationService.success({ key: index_1.T.SPAN_LINK_TWO_WAY_SUCCESS });
                return;
            }
            catch (_error) {
                // Revert target collection's edit state on failure
                if (topological_editor_collection_edit_ctrl_1.TopologicalEditorCollectionEditCtrl.is(ctrl)) {
                    ctrl.revertRegisteredCollection(targetCollectionModel.revision);
                }
                else if (targetCollectionModel.isEditing) {
                    targetCollectionModel.finishEdit();
                }
                services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'span:link:two-way:error', 'unexpected-error', 1);
                notification_service_1.NotificationService.error({ key: index_1.T.SPAN_LINK_ERROR });
                return;
            }
        }
        // Existing one-way link behavior
        field.Mutator.collection(targetCollection.id);
        if (topological_editor_collection_edit_ctrl_1.TopologicalEditorCollectionEditCtrl.is(ctrl) && ctrl.collectionEditState.collectionRevision) {
            point_collection_cache_1.TopologicalPointCollectionCache.dropCollectionVectors(ctrl.collectionEditState.collectionRevision);
            ctrl.redrawMap();
        }
        services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, isSpanContext ? 'span:link:success' : 'vector:link:success', field.schema.displayName, 1);
    });
}
function viewVectorManualEdit(field, ctrl) {
    if (!ctrl.changeState) {
        return null;
    }
    let inputState = ctrl.changeState.getState(field.id);
    if (!inputState) {
        const { value } = field;
        inputState = new vector_input_state_1.VectorInputState(field.schema.displayName);
        inputState.onValue(() => {
            field.Mutator.manual(inputState.value);
            // This will ensure topo map is updated (if it exists) and a user is updating vector locations
            point_collection_cache_1.TopologicalPointCollectionCache.dropCollectionVectors(field.collection.revision);
        });
        ctrl.changeState.addState(field.id, inputState);
        if (value != null) {
            inputState.setValue({
                distance: value.distance,
                bearing: value.bearing,
                pitch: value.pitch,
            });
        }
    }
    // If none of the inputs are focused, and the field model value changes
    // the input state should also update.
    if (field.value !== null &&
        !(inputState.distance.meters.focused ||
            inputState.distance.inch.focused ||
            inputState.distance.feet.focused ||
            inputState.bearing.focused)) {
        inputState.setValue(field.value);
    }
    const onmouseover = () => {
        page_state_1.CurrentState.setHoverId(field.id);
    };
    const onmouseout = () => {
        page_state_1.CurrentState.setHoverId(null);
    };
    const className = input_1.Input.CSS.container;
    return (0, mithril_1.m)('div', { className, onmouseover, onmouseout }, [vector_input_1.VectorInput.view(inputState)]);
}
function viewManualEditButton(field) {
    return field_action_button_1.FieldActionButton.view({
        icon: icons_1.MaterialIcon.EDIT,
        text: index_1.T.FIELD_VECTOR_PROVIDER_MANUAL,
        onclick: () => field.Mutator.manual({ bearing: 0, distance: 0, pitch: 0 }),
    });
}
function viewCollectionLinkButton(field, ctrl) {
    let isDisabled = false;
    if (topological_editor_collection_edit_ctrl_1.TopologicalEditorCollectionEditCtrl.is(ctrl)) {
        isDisabled = ctrl.isMeasurementActive || ctrl.isRelocatePoleActive;
    }
    const isSpanContext = isSpanVectorField(field, ctrl) &&
        (0, core_shared_1.hasCompleteSpanTypeState)(field.parent.schema) &&
        services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_GROUP_IKE_ANALYZE);
    return field_action_button_1.FieldActionButton.view({
        icon: icons_1.MaterialIcon.SPAN_LINK,
        text: isSpanContext ? index_1.T.SPAN_LINK : index_1.T.COLLECTION_LINK,
        linkingText: isSpanContext ? index_1.T.SPAN_LINK_CANCEL : index_1.T.COLLECTION_LINK_CANCEL,
        linking: page_state_1.CurrentState.CollectionLink.isLinking,
        disabled: isDisabled,
        onclick: () => {
            if (page_state_1.CurrentState.CollectionLink.isLinking) {
                page_state_1.CurrentState.CollectionLink.cancelLinking();
                return;
            }
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            linkCollection(field, ctrl);
        },
    });
}
exports.VectorManualFieldView = {
    viewInline(field) {
        if (!field.hasValue()) {
            return null;
        }
        const onmouseover = () => {
            page_state_1.CurrentState.setHoverId(field.id);
        };
        const onmouseout = () => {
            page_state_1.CurrentState.setHoverId(null);
        };
        const className = input_1.Input.CSS.container;
        return (0, mithril_1.m)('div', { className, onmouseover, onmouseout }, [field_info_1.FieldInfo.viewVectorInline(field.value)]);
    },
    hasDetail(field) {
        return false;
    },
    viewDetail(field) {
        return null;
    },
    viewEdit(field, ctrl) {
        // Cast to editable controller - viewEdit is only called when isEditing=true
        const editCtrl = ctrl;
        if (field.hasValue()) {
            return viewVectorManualEdit(field, editCtrl);
        }
        if (!(services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_COLLECTION_LINK) || services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_GROUP_IKE_ANALYZE))) {
            return viewVectorManualEdit(field, editCtrl);
        }
        const children = [];
        if (page_state_1.CurrentState.CollectionLink.isLinking) {
            if (page_state_1.CurrentState.CollectionLink.fieldId === field.id) {
                children.push(viewCollectionLinkButton(field, editCtrl));
            }
        }
        else {
            children.push(viewManualEditButton(field));
            children.push(viewCollectionLinkButton(field, editCtrl));
        }
        const onmouseover = () => {
            page_state_1.CurrentState.setHoverId(field.id);
        };
        const onmouseout = () => {
            page_state_1.CurrentState.setHoverId(null);
        };
        return (0, mithril_1.m)('div', { className: `${CSS.container} ${CSS.isLinked}`, onmouseover, onmouseout }, children);
    },
};
//# sourceMappingURL=vector.manual.field.view.js.map